import { useState, useEffect } from "react";
import { toast } from "sonner";
import { useForm } from "react-hook-form";
import { Eye, EyeOff, Mail, LockKeyhole, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import { ucApi, AuthMethod} from "@/services/api/uc";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { ThirdPartyLogin } from "./ThirdPartyLogin";
import { useTranslation } from "react-i18next";
import { NETWORK_ERROR_MESSAGE } from "@/utils/axios";

const formSchema = z.object({
  email: z.string().email("Please enter a valid email address"),
  password: z.string().min(6, "Password must be at least 6 characters"),
});

interface LoginFormProps {
  onLoginSuccess?: (data: any, email: string, password: string, authType?: string) => void;
  onForgotPassword?: () => void;
  onAgreementError?: () => void;
  initialEmail?: string;
  redirectUrl?: string;
  agreementAccepted?: boolean;
}

export function LoginForm({ onLoginSuccess, onForgotPassword, onAgreementError, initialEmail = "", redirectUrl = "", agreementAccepted = false }: LoginFormProps) {
  const { login } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const { t } = useTranslation();

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: initialEmail || localStorage.getItem('anzir_lastEmail') || "",
      password: "",
    },
  });

  useEffect(() => {
    if (initialEmail) {
      form.setValue('email', initialEmail);
    }
  }, [initialEmail, form]);


  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    setError("");

    if (!agreementAccepted) {
      setError("You must agree to our terms and conditions");
      toast.error("Terms not accepted", {
        description: "You must agree to our terms and conditions to continue"
      });
      if (onAgreementError) {
        onAgreementError();
      }
      return;
    }

    setIsSubmitting(true);

    try {
      localStorage.setItem('anzir_lastEmail', values.email);

      // 调用AuthContext中的登录方法，传入标识范围和身份验证方法
      const loginResponse = await login(values.email, values.password, "player", AuthMethod.EmailPwd);

      // 登录成功，获取登录响应数据
      const userData = loginResponse.data;

      // 检查用户是否设置了OTP登录
      try {
        const otpSettingsResponse = await ucApi.auth.emailAuth({ email: values.email });
        console.log('Email OTP settings:', otpSettingsResponse.data);

        // 检查authType是否为OTP，如果是则需要二步验证
        if (otpSettingsResponse.data &&
            otpSettingsResponse.data.authType === "OTP") {
          // 用户已设置OTP登录，需要打开二步验证弹窗
          console.log('用户已设置OTP登录，需要二步验证');
          if (onLoginSuccess) {
            // 传递用户数据到登录成功回调，由父组件处理打开OTP验证弹窗
            onLoginSuccess(userData, values.email, values.password, otpSettingsResponse.data.authType);
          }
        } else {
          // 用户未设置OTP登录，直接处理登录成功
          console.log('用户未设置OTP登录，直接登录成功');

          // 通知登录成功
          if (onLoginSuccess) {
            onLoginSuccess(userData, values.email, values.password, otpSettingsResponse.data?.authType);
          }
        }
      } catch (otpError) {
        console.error('获取Email OTP设置失败:', otpError);
        // OTP设置查询失败，仍然通知登录成功
        if (onLoginSuccess) {
          onLoginSuccess(userData, values.email, values.password);
        }
      }
    } catch (error: any) {
      console.error("Login error:", error);

      // 处理各种错误情况
      let errorMessage = t("auth.errors.loginFailed", "Invalid credentials");

      if (error.message) {
        if (error.message === NETWORK_ERROR_MESSAGE) {
          errorMessage = t("auth.errors.networkError", "Network error, please try again later");
        } else if (error.message.includes("Email does not exist")) {
          errorMessage = t("auth.errors.loginFailed", "Invalid email or password");
        } else {
          errorMessage = error.message;
        }
      }

      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Email</FormLabel>
              <div className="relative">
                <Mail className="absolute left-3 top-3 h-4 w-4 text-gray-500" />
                <FormControl>
                  <Input
                    placeholder="yourname@example.com"
                    className="pl-10"
                    {...field}
                  />
                </FormControl>
              </div>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="password"
          render={({ field }) => (
            <FormItem>
              <div className="flex justify-between">
                <FormLabel>Password</FormLabel>
                {onForgotPassword && (
                  <Button
                    variant="link"
                    size="sm"
                    className="p-0 h-auto text-xs"
                    onClick={(e) => {
                      e.preventDefault();
                      onForgotPassword();
                    }}
                    type="button"
                  >
                    Forgot Password?
                  </Button>
                )}
              </div>
              <div className="relative">
                <LockKeyhole className="absolute left-3 top-3 h-4 w-4 text-gray-500" />
                <FormControl>
                  <Input
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter your password"
                    className="pl-10 pr-10 bg-black/50 border-white/20 text-white"
                    {...field}
                  />
                </FormControl>
                <div
                  className="absolute right-0 top-0 h-10 w-10 flex items-center justify-center cursor-pointer group"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4 text-gray-500 group-hover:text-[#ff6c24] transition-colors" />
                  ) : (
                    <Eye className="h-4 w-4 text-gray-500 group-hover:text-[#ff6c24] transition-colors" />
                  )}
                </div>
              </div>
              <FormMessage />
            </FormItem>
          )}
        />

        {error && (
          <div className="bg-red-500/10 text-red-500 px-4 py-2 rounded flex items-center gap-2">
            <AlertCircle className="h-4 w-4" />
            <span className="text-sm">{error}</span>
          </div>
        )}

        <Button
          type="submit"
          className="w-full bg-[#ff6c24] hover:bg-[#ff6c24]/90"
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <span className="h-5 w-5 border-2 border-white border-t-transparent rounded-full inline-block animate-spin mr-2"></span>
          ) : null}
          Sign In
        </Button>
      </form>
      <div className="mt-4">
        <ThirdPartyLogin 
          variant="default" 
          size="default" 
          agreementAccepted={agreementAccepted} 
          onAgreementError={onAgreementError}
        />
      </div>
    </Form>
  );
}
